import React from "react";
import "./CommonNewFunnel.css";

export type FunnelStep = "start" | "details" | "eligibility";

type Props = {
  step: FunnelStep;
  onBack?: () => void;

  /**
   * If provided, controls whether the back button shows.
   * If not provided, it falls back to "show only when phase is not the first dot".
   */
  showBack?: boolean;
};

const STEPS: { id: FunnelStep; label: string }[] = [
  { id: "start", label: "Start" },
  { id: "details", label: "Details" },
  { id: "eligibility", label: "Eligibility" },
];

const FunnelProgress: React.FC<Props> = ({ step, onBack, showBack }) => {
  const currentIndex = STEPS.findIndex((s) => s.id === step);

  // default behavior: show back only after the first phase
  // but allow FunnelLayout to override (ex: show on step-2 even if phase is "start")
  const canShowBack =
    typeof showBack === "boolean" ? showBack : currentIndex > 0;

  return (
    <div className="funnel-progress">
      {canShowBack && !!onBack && (
        <button
          className="funnel-progress__back"
          onClick={onBack}
          aria-label="Back"
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="15"
            viewBox="0 0 18 15"
            fill="none"
          >
            <path
              d="M0.292892 6.65686C-0.0976315 7.04739 -0.0976315 7.68055 0.292892 8.07107L6.65685 14.435C7.04738 14.8256 7.68054 14.8256 8.07107 14.435C8.46159 14.0445 8.46159 13.4113 8.07107 13.0208L2.41421 7.36397L8.07107 1.70711C8.46159 1.31659 8.46159 0.683424 8.07107 0.2929C7.68054 -0.0976243 7.04738 -0.0976243 6.65685 0.2929L0.292892 6.65686ZM18 7.36397V6.36397L1 6.36397V7.36397V8.36397L18 8.36397V7.36397Z"
              fill="black"
            />
          </svg>
        </button>
      )}

      <div className="funnel-progress__steps">
        {STEPS.map((s, index) => {
          const isCompleted = index < currentIndex;
          const isActive = index === currentIndex;

          return (
            <React.Fragment key={s.id}>
              <div className="funnel-progress__step">
                <div
                  className={[
                    "step-circle",
                    isCompleted && "is-completed",
                    isActive && "is-active",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {isCompleted ? "✓" : isActive ? <span className="dot" /> : null}
                </div>

                <span
                  className={[
                    "step-label",
                    (isActive || isCompleted) && "is-highlighted",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {s.label}
                </span>
              </div>

              {index < STEPS.length - 1 && (
                <div
                  className={[
                    "step-line",
                    index < currentIndex && "is-filled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default FunnelProgress;